import { Search } from "@mui/icons-material";
import { Box, Button, Grid, InputAdornment, Stack, TextField } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { usePaginationState } from "../../../App/Components/Table/State/PaginationState";
import { useGetClientEmailTemplatesQuery } from "../../../App/Queries/MessageQueries";
import { useAppSelector } from "../../../App/State/Hooks/reduxHooks";
import { useDebouncedHandler } from "../../../App/State/Hooks/useDebouncedHandler";
import { selectClientValue } from "../../../App/State/Slices/SelectedClientSlice";
import { IFeatureToggles } from "../../../App/Typings/FeatureToggles";
import { ICandidateWithMessageInfo, IEmailTemplate, IMessageDetail } from "../../../App/Typings/MessageText";
import { ITableColumn } from "../../../App/Typings/TableColumn";
import MessageTemplateTableActions from "../../../MessageComponents/MessageTemplates/Components/MessageTemplateTableActions";
import { truncateMessageBody } from "../../../MessageComponents/MessageTemplates/EmailTemplateListDialog";
import { MessageTemplateTable } from "../../../MessageComponents/MessageTemplates/MessageTemplateTable";
import ViewEmailModal from "../../EmailMarketing/Components/ViewEmailModal";
import { MessagesContextProvider } from "../../State/MessagesContext";
import { DeleteEmailTemplateModal } from "../Shared/DeleteTemplateModals";
import { DraftEmailTemplateModal } from "./DraftEmailTemplateModal";

export const DisplayEmailTemplates = () => {
  const { useCustomEmailDomain } = useFlags<IFeatureToggles>();
  const { clientName, clientEmailAddress, senderEmailDomain } = useAppSelector(selectClientValue);
  const [selectedDeleteTemplate, setSelectedDeleteTemplate] = useState<IEmailTemplate | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [action, setAction] = useState<"create" | "edit" | "duplicate">("create");
  const [selectedEditTemplate, setSelectedEditTemplate] = useState<IEmailTemplate | null>(null);
  const [isDraftDialogOpen, setIsDraftDialogOpen] = useState(false);
  const {
    getters: { page, pageSize, search, sortBy, sortDirection },
    handlers: { handleChangePage, handleChangePageSize, handleSearchChange, handleRequestSort },
  } = usePaginationState({ sortBy: "modifiedDate", sortDirection: "desc" }, "email-templates");

  const { data: emailTemplates = [], isLoading: isLoadingEmailTemplates } =
    useGetClientEmailTemplatesQuery(search);

  let fromAddress = clientEmailAddress;
  if (useCustomEmailDomain && senderEmailDomain) {
    fromAddress = `recruiter@${senderEmailDomain}`;
  }

  const [showPreview, setShowPreview] = useState(false);
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState<IEmailTemplate | null>(null);
  const [previewTemplateView, setPreviewTemplateView] = useState<IMessageDetail | null>(null);
  const previewCandidate: Partial<ICandidateWithMessageInfo> = {
    firstName: "Preview",
    lastName: "Candidate",
  };

  const handleInputSearch = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e);
  }, 200);

  const handleCreateTemplate = () => {
    setAction("create");
    setIsDraftDialogOpen(true);
  };

  const handleDuplicateClick = (template: IEmailTemplate) => {
    setAction("duplicate");
    const fullTemplate = {
      ...emailTemplates.find((t) => t.emailTemplateId === template.emailTemplateId),
    } as IEmailTemplate;
    if (!fullTemplate) {
      return;
    }
    fullTemplate.name = `${fullTemplate.name} (1)`;
    fullTemplate.emailTemplateId = "";

    setSelectedEditTemplate(fullTemplate);
    setIsDraftDialogOpen(true);
  };

  const handleSelectEditTemplate = (template: IEmailTemplate) => {
    const fullTemplate = emailTemplates.find((t) => t.emailTemplateId === template.emailTemplateId);
    if (!fullTemplate) {
      return;
    }
    setAction("edit");
    setSelectedEditTemplate(fullTemplate);
    setIsDraftDialogOpen(true);
  };

  const handleSelectDeleteTemplate = (template: IEmailTemplate) => {
    const fullTemplate = emailTemplates.find((t) => t.emailTemplateId === template.emailTemplateId);
    setSelectedDeleteTemplate(fullTemplate as IEmailTemplate);
    setIsDeleteDialogOpen(true);
  };

  const handlePreview = (row: IEmailTemplate) => {
    const originalRow = emailTemplates?.find((template) => template.emailTemplateId === row.emailTemplateId);
    if (!originalRow) {
      setSelectedPreviewTemplate(null);
      setPreviewTemplateView(null);
      setShowPreview(false);
      return;
    }
    setPreviewTemplateView({
      id: originalRow.emailTemplateId,
      toAddress: `${previewCandidate.firstName?.toLowerCase()}@example.com`,
      toName: previewCandidate.firstName + " " + previewCandidate.lastName,
      fromAddress,
      fromName: clientName,
      message: originalRow.body,
      subject: originalRow.subject,
      messageType: "DirectEmailMessage",
      replyToMessageId: "",
      userHasReplyNotification: false,
    });
    setSelectedPreviewTemplate(originalRow);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
    setTimeout(() => {
      setPreviewTemplateView(null);
    }, 300);
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Stack
          sx={{ width: 1, mb: 2 }}
          direction={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "flex-start", md: "space-between" }}
          alignItems="center"
          gap={{ xs: 2, md: 0 }}
        >
          <TextField
            data-test="email-templates-searchbar"
            label="Search"
            defaultValue={search}
            type="search"
            fullWidth
            sx={{ width: "40ch", maxWidth: "100%" }}
            onChange={handleInputSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ width: { xs: "40ch", md: "auto" }, maxWidth: "100%" }}>
            <Button onClick={handleCreateTemplate} variant="contained" fullWidth>
              Create Email Template
            </Button>
          </Box>
        </Stack>
      </Grid>
      <Grid item>
        <MessageTemplateTable
          name="email"
          columns={emailTemplateColumns}
          loading={isLoadingEmailTemplates}
          hoverRowEffect={false}
          tableData={emailTemplates.map((c) => ({
            ...truncateEmailTemplateBody(c),
            actions: (
              <MessageTemplateTableActions
                id={c.emailTemplateId}
                onDeleteClick={() => handleSelectDeleteTemplate(c)}
                onEditClick={() => handleSelectEditTemplate(c)}
                onPreviewClick={() => handlePreview(c)}
                onDuplicateClick={() => handleDuplicateClick(c)}
              />
            ),
          }))}
          paginationOptions={{
            currentPage: page,
            rows: pageSize,
            handleChangePage,
            handleChangeRowsPerPage: handleChangePageSize,
          }}
          customOrder={sortDirection}
          customOrderBy={sortBy}
          customRequestSort={(_, property) => handleRequestSort(property)}
        />
      </Grid>
      <DraftEmailTemplateModal
        isDraftDialogOpen={isDraftDialogOpen}
        setIsDraftDialogOpen={setIsDraftDialogOpen}
        action={action}
        editTemplate={selectedEditTemplate}
        setEditTemplate={setSelectedEditTemplate}
      />
      <DeleteEmailTemplateModal
        deleteTemplate={selectedDeleteTemplate}
        setDeleteTemplate={setSelectedDeleteTemplate}
        isDeleteDialogOpen={isDeleteDialogOpen}
        setIsDeleteDialogOpen={setIsDeleteDialogOpen}
      />
      <MessagesContextProvider
        previewCandidate={previewCandidate}
        previewMessageDetail={previewTemplateView ?? undefined}
      >
        <ViewEmailModal
          showPreview={showPreview}
          onClose={handleClosePreview}
          size="md"
          title={`Preview Email Template: ${selectedPreviewTemplate?.name || ""}`}
        />
      </MessagesContextProvider>
    </Grid>
  );
};

const emailTemplateColumns: ITableColumn[] = [
  { label: "Created", propName: "createdDate", order: 0 },
  { label: "Name", propName: "name", order: 1 },
  { label: "Subject", propName: "subject", order: 2 },
  { label: "Body", propName: "body", order: 3 },
  { label: "Visibility", propName: "visibility", order: 4 },
  { label: "Modified", propName: "modifiedDate", order: 5 },
  { label: "Actions", propName: "actions", order: 6 },
];

const truncateEmailTemplateBody = (template: IEmailTemplate) => ({
  ...template,
  body: truncateMessageBody(template.body),
});
